import { useEffect, useRef } from 'react';
import { useCookieConsent } from './CookieConsentContext';

const AD_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT_ID;
const IN_ARTICLE_SLOT = process.env.REACT_APP_ADSENSE_IN_ARTICLE_SLOT;

/**
 * Renders a single Google AdSense ad unit.
 *
 * Only renders when:
 *  - REACT_APP_ADSENSE_CLIENT_ID is set
 *  - The user has accepted all cookies (consent === 'all')
 *
 * Usage:
 *   <AdSenseAd type="in-article" />
 */
const AdSenseAd = ({ type = 'in-article', className = '' }) => {
  const { consent, loading } = useCookieConsent();
  const insRef = useRef(null);
  const pushed = useRef(false);

  useEffect(() => {
    if (loading || consent !== 'all' || !AD_CLIENT || pushed.current) return;
    if (!insRef.current) return;
    // Guard against the same <ins> element being pushed twice.
    // adsbygoogle sets data-adsbygoogle-status after processing.
    if (insRef.current.dataset.adsbygoogleStatus) return;
    try {
      pushed.current = true;
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      // adsbygoogle not ready yet — AdSenseConsentGate will load the script
    }
  }, [consent, loading]);
  // Note: no cleanup to reset pushed.current — intentional.
  // React StrictMode mounts→unmounts→remounts the same instance; keeping
  // pushed.current=true across that cycle prevents the double-push error.
  // Navigating to a new page creates a fresh component instance with a fresh ref.

  if (!AD_CLIENT || loading || consent !== 'all') return null;

  if (type === 'in-article' && IN_ARTICLE_SLOT) {
    return (
      <div className={`adsense-in-article-wrapper ${className}`.trim()}>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block', textAlign: 'center' }}
          data-ad-layout="in-article"
          data-ad-format="fluid"
          data-ad-client={AD_CLIENT}
          data-ad-slot={IN_ARTICLE_SLOT}
        />
      </div>
    );
  }

  return null;
};

export default AdSenseAd;
